import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useBranding } from "@/contexts/BrandingContext";
import { usePresentationMode } from "@/contexts/PresentationModeContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PageDateFilter from "@/components/PageDateFilter";
import PageNote from "@/components/PageNote";
import {
  Linkedin,
  Users,
  TrendingUp,
  TrendingDown,
  Heart,
  MessageCircle,
  Share2,
  Eye,
  MousePointerClick,
  RefreshCw,
  AlertCircle,
  Monitor,
  Smartphone,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

// Component to show period-over-period change - moved outside main component
const ChangeIndicator = ({ change, suffix = "%", isPercentagePoint = false, comparisonLabel = "vs prior" }) => {
  if (change === null || change === undefined) return null;
  
  const isPositive = change > 0;
  const isNegative = change < 0;
  
  return (
    <span className={`inline-flex items-center text-xs font-medium ml-2 ${
      isPositive ? "text-emerald-600" : isNegative ? "text-red-500" : "text-slate-500"
    }`}>
      {isPositive && <TrendingUp className="w-3 h-3 mr-0.5" />}
      {isNegative && <TrendingDown className="w-3 h-3 mr-0.5" />}
      {isPositive && "+"}{change}{suffix}
      {!isPercentagePoint && ` ${comparisonLabel}`}
    </span>
  );
};

const SocialInsightsPage = () => {
  const { sessionToken, permissions, isSuperAdmin } = useAuth();
  const { chartColors, pageTitles } = useBranding();
  const { isPresentationMode, maskNumber, maskPercent } = usePresentationMode();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [linkedInData, setLinkedInData] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isMockData, setIsMockData] = useState(true);
  const [dateFilter, setDateFilter] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [tenantSettings, setTenantSettings] = useState({});
  
  // LinkedIn Ads data
  const [linkedInAdsData, setLinkedInAdsData] = useState(null);
  const [adsLoading, setAdsLoading] = useState(false);
  const [comparisonMode, setComparisonMode] = useState("prior"); // "none", "prior", "yoy"
  
  // DYNAMIC TENANT RESOLUTION - No hardcoded IDs!
  // Tenant-specific behavior is determined by tenant name from branding context, not hardcoded UUIDs
  const [homeTenantId, setHomeTenantId] = useState(null);
  
  // CRITICAL: Track tenant ID in state to trigger re-fetches when tenant changes
  // sessionStorage changes don't trigger React re-renders
  const [currentTenantId, setCurrentTenantId] = useState(() => sessionStorage.getItem("viewing_tenant_id"));
  
  // Fetch home tenant dynamically on mount
  useEffect(() => {
    const fetchHomeTenant = async () => {
      if (!sessionToken) return;
      try {
        const response = await axios.get(`${API}/tenants/default-home`, {
          params: { Authorization: `Bearer ${sessionToken}` }
        });
        if (response.data?.tenant_id) {
          setHomeTenantId(response.data.tenant_id);
        }
      } catch (err) {
        console.error("Failed to fetch home tenant:", err);
      }
    };
    fetchHomeTenant();
  }, [sessionToken]);
  
  // Derived tenant flags - use tenant NAME from branding context instead of hardcoded IDs
  // This ensures it works regardless of which database (test/production) is connected
  const tenantName = sessionStorage.getItem("viewing_tenant_name")?.toLowerCase() || "";
  const isInventAiTenant = tenantName.includes("invent");
  const isViewingJCI = !currentTenantId || tenantName.includes("jci") || (homeTenantId && currentTenantId === homeTenantId);
  
  // Check if LinkedIn Ads is connected for this tenant
  const [hasLinkedInAds, setHasLinkedInAds] = useState(false);
  
  // Check visibility settings (default true, can be disabled per tenant by super admin)
  const showPageViewsChart = tenantSettings?.show_page_views_chart !== false;
  const showEngagementChart = tenantSettings?.show_engagement_chart !== false;
  const showPageViewsTable = tenantSettings?.show_page_views_table !== false;
  const showAdditionalMetrics = tenantSettings?.show_additional_metrics !== false;
  
  // Super admin can refresh only when viewing JCI tenant
  const canRefresh = isSuperAdmin && isViewingJCI;
  
  // Listen for tenant changes via storage events and visibility changes
  useEffect(() => {
    const checkTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (newTenantId !== currentTenantId) {
        console.log("=== SOCIAL INSIGHTS: TENANT CHANGED ===", currentTenantId, "->", newTenantId);
        setCurrentTenantId(newTenantId);
        // Reset data to force re-fetch
        setLinkedInData(null);
        setLinkedInAdsData(null);
        setInitialLoadDone(false);
        setLoading(true);
      }
    };
    
    // Check on visibility change (when user navigates back to this tab/page)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkTenantChange();
      }
    };
    
    // Check periodically for tenant changes (handles navigation within app)
    const interval = setInterval(checkTenantChange, 500);
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', checkTenantChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', checkTenantChange);
    };
  }, [currentTenantId]);

  // Fetch tenant settings - use display-settings endpoint which allows auth users
  // CRITICAL: Now includes currentTenantId as dependency to re-fetch when tenant changes
  useEffect(() => {
    const fetchTenantSettings = async () => {
      // Use the state variable which is synced with sessionStorage
      const viewingTenantId = currentTenantId;
      
      if (viewingTenantId) {
        try {
          // First try to get from main tenant endpoint (super admin)
          const response = await axios.get(`${API}/tenants/${viewingTenantId}`, {
            params: { Authorization: `Bearer ${sessionToken}` }
          });
          setTenantSettings(response.data.settings || {});
        } catch (error) {
          // Fallback to display-settings endpoint for non-super admins
          try {
            const displayResponse = await axios.get(`${API}/tenants/${viewingTenantId}/display-settings`, {
              params: { Authorization: `Bearer ${sessionToken}` }
            });
            // Map display_settings to settings format
            setTenantSettings({
              show_page_views_chart: displayResponse.data.show_page_views_chart ?? true,
              show_engagement_chart: displayResponse.data.show_engagement_chart ?? true,
              show_page_views_table: displayResponse.data.show_page_views_table ?? true,
              show_additional_metrics: displayResponse.data.show_additional_metrics ?? true
            });
          } catch (fallbackError) {
            console.error("Error fetching tenant settings:", fallbackError);
          }
        }
      }
    };
    fetchTenantSettings();
  }, [sessionToken, currentTenantId]); // CRITICAL: Added currentTenantId

  const fetchLinkedInAnalytics = async (startDate, endDate) => {
    // Use currentTenantId from state (which is kept in sync with sessionStorage)
    const viewingTenantId = currentTenantId;
    console.log("=== fetchLinkedInAnalytics called ===");
    console.log("startDate:", startDate, "endDate:", endDate);
    console.log("currentTenantId (from state):", viewingTenantId);
    
    try {
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
      
      // ALWAYS use cached endpoint - NO live API calls
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      
      console.log("Fetching from LinkedIn cache (no live API calls)...");
      params.comparison_mode = comparisonMode;
      const cacheResponse = await axios.get(`${API}/cache/linkedin/cached-analytics`, { params });
      console.log("Cached LinkedIn analytics response:", cacheResponse.data);
      
      if (cacheResponse.data.cached) {
        // Cache hit - use cached data
        setIsConnected(cacheResponse.data.connected);
        setIsMockData(false);
        setLastSyncTime(cacheResponse.data.date_range?.end || null);
        setLinkedInData({
          followers: cacheResponse.data.followers,
          follower_gains: cacheResponse.data.follower_gains,
          organic_followers: cacheResponse.data.organic_followers,
          paid_followers: cacheResponse.data.paid_followers,
          impressions: cacheResponse.data.impressions,
          clicks: cacheResponse.data.clicks,
          likes: cacheResponse.data.likes,
          comments: cacheResponse.data.comments,
          shares: cacheResponse.data.shares,
          page_views: cacheResponse.data.page_views,
          engagement_rate: cacheResponse.data.engagement_rate,
          organization: cacheResponse.data.organization,
          daily_breakdown: cacheResponse.data.daily_breakdown,
          changes: cacheResponse.data.changes || {}
        });
      } else if (cacheResponse.data.connected) {
        // Connected but no cached data - show message to run backfill
        setIsConnected(true);
        setIsMockData(false);
        setLinkedInData({
          followers: 0,
          follower_gains: 0,
          impressions: 0,
          clicks: 0,
          likes: 0,
          comments: 0,
          shares: 0,
          page_views: 0,
          engagement_rate: 0,
          changes: {},
          message: "No cached data available. Data syncs daily at midnight UTC."
        });
      } else {
        // Not connected
        setIsConnected(false);
        setIsMockData(false);
      }
    } catch (error) {
      console.error("Error fetching LinkedIn analytics from cache:", error);
      // On error, show not connected state - NO fallback to live API
      setIsConnected(false);
      setIsMockData(false);
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  };

  // Fetch LinkedIn Ads data (paid impressions)
  const fetchLinkedInAdsData = async (startDate, endDate) => {
    setAdsLoading(true);
    try {
      // Use currentTenantId from state
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
      
      // Use cached endpoint - only calls LinkedIn API once per 24 hours
      const response = await axios.get(`${API}/cache/linkedin-ads/cached-metrics`, { params });
      
      // Transform cached response to match expected format
      if (response.data?.totals) {
        setLinkedInAdsData({
          connected: response.data.connected,
          impressions: response.data.totals.impressions || 0,
          clicks: response.data.totals.clicks || 0,
          spend: response.data.totals.spend || 0,
          conversions: response.data.totals.conversions || 0,
          daily_data: response.data.data || [],
          cache_age_hours: response.data.cache_age_hours,
          account: response.data.account
        });
      } else {
        setLinkedInAdsData(response.data);
      }
    } catch (error) {
      console.error("Error fetching LinkedIn Ads data:", error);
      setLinkedInAdsData(null);
    } finally {
      setAdsLoading(false);
    }
  };

  const syncLinkedInData = async (startDate, endDate) => {
    if (!isConnected) {
      toast.error("LinkedIn is not connected. Go to Admin Panel to connect.");
      return;
    }
    
    setSyncing(true);
    try {
      const response = await axios.post(
        `${API}/integrations/linkedin/sync`,
        { start_date: startDate, end_date: endDate },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      setLinkedInData(response.data);
      setIsMockData(false);
      toast.success("Data updated for selected date range");
    } catch (error) {
      console.error("Error syncing LinkedIn data:", error);
      toast.error(error.response?.data?.detail || "Failed to sync LinkedIn data");
    } finally {
      setSyncing(false);
    }
  };

  // Handle date filter change - just update state, filter locally
  const handleDateChange = useCallback((dateInfo) => {
    setDateFilter(dateInfo);
  }, []);

  // Fetch LinkedIn Ads data when date filter changes
  // Now uses cached endpoint - safe to call on every date change (no live API calls)
  useEffect(() => {
    if (dateFilter && hasLinkedInAds) {
      fetchLinkedInAdsData(dateFilter.startDate, dateFilter.endDate);
    }
  }, [dateFilter?.startDate, dateFilter?.endDate, hasLinkedInAds]);

  // Auto-fetch LinkedIn data when date filter changes
  // Backend now handles caching - only calls LinkedIn API if data for period isn't cached
  // CRITICAL: Added currentTenantId dependency to re-fetch when tenant changes
  useEffect(() => {
    console.log("Date filter effect triggered:", dateFilter?.startDate, dateFilter?.endDate, "initialLoadDone:", initialLoadDone, "tenant:", currentTenantId);
    if (dateFilter && initialLoadDone) {
      console.log("Fetching LinkedIn analytics for new date range...");
      setLoading(true); // Show loading while fetching period data
      fetchLinkedInAnalytics(dateFilter.startDate, dateFilter.endDate);
    }
  }, [dateFilter?.startDate, dateFilter?.endDate, initialLoadDone, currentTenantId, comparisonMode]);

  // Check if LinkedIn Ads is connected for this tenant
  // CRITICAL: Added currentTenantId dependency
  useEffect(() => {
    const checkLinkedInAdsStatus = async () => {
      try {
        // Use currentTenantId from state
        const viewingTenantId = currentTenantId;
        const params = { Authorization: `Bearer ${sessionToken}` };
        if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
        
        const response = await axios.get(`${API}/integrations/status`, { params });
        setHasLinkedInAds(response.data?.linkedin_ads?.connected === true);
      } catch (error) {
        console.error("Error checking LinkedIn Ads status:", error);
        setHasLinkedInAds(false);
      }
    };
    
    if (sessionToken) {
      checkLinkedInAdsStatus();
    }
  }, [sessionToken, currentTenantId]);

  // Initial load - CRITICAL: Now depends on currentTenantId to re-fetch when tenant changes
  useEffect(() => {
    if (!sessionToken) {
      console.log("Waiting for sessionToken");
      return;
    }
    if (!dateFilter?.startDate || !dateFilter?.endDate) {
      console.log("Waiting for dateFilter to be set");
      setInitialLoadDone(true);
      setLoading(false); // Break deadlock: allow page (incl. DateRangeFilter) to render so it can set the date
      return;
    }
    console.log("Initial LinkedIn fetch, tenant:", currentTenantId, "dates:", dateFilter.startDate, dateFilter.endDate);
    fetchLinkedInAnalytics(dateFilter.startDate, dateFilter.endDate);
  }, [sessionToken, currentTenantId]);

  // Fetch ads data when hasLinkedInAds becomes true (initial load)
  useEffect(() => {
    if (hasLinkedInAds && sessionToken) {
      fetchLinkedInAdsData();
    }
  }, [hasLinkedInAds, sessionToken, currentTenantId]);

  // Format number - use maskNumber in presentation mode, otherwise show with commas
  const formatNumber = (num) => {
    if (num === null || num === undefined) return isPresentationMode ? "—" : "0";
    if (isPresentationMode) {
      return maskNumber(num);
    }
    return num.toLocaleString();
  };

  // Prepare engagement data for chart - using branding colors
  const engagementData = linkedInData ? [
    { name: "Likes", value: linkedInData.likes || 0, color: chartColors[0] || "#0A66C2" },
    { name: "Comments", value: linkedInData.comments || 0, color: chartColors[1] || "#00A5A8" },
    { name: "Shares", value: linkedInData.shares || 0, color: chartColors[2] || "#10B981" },
    { name: "Clicks", value: linkedInData.clicks || 0, color: chartColors[3] || "#F59E0B" },
  ] : [];

  // Prepare page views data for chart
  const pageViewsData = linkedInData?.page_views ? [
    { name: "Overview", views: linkedInData.page_views.overview || 0 },
    { name: "About", views: linkedInData.page_views.about || 0 },
    { name: "People", views: linkedInData.page_views.people || 0 },
    { name: "Jobs", views: linkedInData.page_views.jobs || 0 },
  ] : [];

  // Comparison label
  const socialComparisonLabel = comparisonMode === "yoy" ? "vs year ago" : "vs prior";
  const showChanges = comparisonMode !== "none";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="social-insights-page">
      {/* Page Header */}
      <div className="page-header flex-wrap gap-4">
        <div className="flex-1">
          <h1 className="page-title flex items-center gap-2">
            <Linkedin className="w-6 h-6 text-[#0A66C2]" />
            {pageTitles?.social_insights || "Social Insights"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            LinkedIn engagement and social media performance
          </p>
        </div>
        
        {/* Date Filter & Status Badges */}
        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-3">
          {/* Date Range Selector */}
          <PageDateFilter 
            pageId="social-insights" 
            onDateChange={handleDateChange}
            showTimePeriodBadge={true}
          />
          
          {/* Status Badges */}
          <div className="flex items-center gap-2">
            {/* Comparison Mode Toggle */}
            <Select value={comparisonMode} onValueChange={setComparisonMode}>
              <SelectTrigger className="w-[155px]" data-testid="social-comparison-mode">
                <SelectValue placeholder="Comparison" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Comparison</SelectItem>
                <SelectItem value="prior">vs Prior Period</SelectItem>
                <SelectItem value="yoy">vs Year Ago</SelectItem>
              </SelectContent>
            </Select>
            {isMockData && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                <AlertCircle className="w-3 h-3 mr-1" />
                Sample Data
              </Badge>
            )}
            {/* Only super admins viewing JCI can trigger sync - LinkedIn has 24h rate limit */}
            {canRefresh && (
              <Button 
                onClick={() => syncLinkedInData(dateFilter?.startDate, dateFilter?.endDate)} 
                disabled={syncing || !isConnected}
                variant="outline"
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Syncing..." : "Refresh"}
              </Button>
            )}
            {lastSyncTime && (
              <span className="text-xs text-slate-500">
                Last synced: {new Date(lastSyncTime).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Page Notes & Market Insights */}
      <PageNote pageId="social" />

      {/* Connection Status Banner */}
      {!isConnected && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">LinkedIn Not Connected</p>
                <p className="text-sm text-amber-600">
                  Connect your LinkedIn page in the Admin Panel to see real data.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Total Followers</p>
                <div className="flex items-baseline">
                  <p className="kpi-value">{formatNumber(linkedInData?.followers || 0)}</p>
                  <ChangeIndicator change={showChanges ? linkedInData?.changes?.followers : undefined} comparisonLabel={socialComparisonLabel} />
                </div>
                {linkedInData?.follower_growth && (
                  <p className="text-xs text-slate-500">
                    +{formatNumber(linkedInData.follower_growth)} this period
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#0A66C2]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#0A66C2]" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* New Followers for Period */}
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">New Followers</p>
                <div className="flex items-baseline">
                  <p className="kpi-value">{formatNumber(linkedInData?.follower_gains || 0)}</p>
                  <ChangeIndicator change={showChanges ? linkedInData?.changes?.follower_gains : undefined} comparisonLabel={socialComparisonLabel} />
                </div>
                <p className="text-xs text-slate-500">
                  This period {socialComparisonLabel}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Organic Impressions</p>
                <div className="flex items-baseline">
                  <p className="kpi-value">{formatNumber(linkedInData?.impressions || 0)}</p>
                  <ChangeIndicator change={showChanges ? linkedInData?.changes?.impressions : undefined} comparisonLabel={socialComparisonLabel} />
                </div>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Eye className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Paid Impressions - For all tenants with LinkedIn Ads connected */}
        {hasLinkedInAds && (
          <Card className="kpi-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="kpi-label">Paid Impressions</p>
                  <div className="flex items-baseline">
                    {adsLoading ? (
                      <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                    ) : linkedInAdsData?.impressions > 0 ? (
                      <>
                        <p className="kpi-value">{formatNumber(linkedInAdsData.impressions)}</p>
                        {linkedInAdsData.ctr > 0 && (
                          <span className="text-xs text-slate-500 ml-2">
                            {linkedInAdsData.ctr}% CTR
                          </span>
                        )}
                      </>
                    ) : linkedInAdsData?.account_selected === false ? (
                      <p className="text-sm text-amber-600">No Ad Account selected</p>
                    ) : linkedInAdsData?.connected === false ? (
                      <p className="text-sm text-slate-500">LinkedIn Ads not connected</p>
                    ) : (
                      <p className="text-sm text-slate-500">No paid impression data</p>
                    )}
                  </div>
                  {linkedInAdsData?.impressions > 0 && (
                    <p className="text-xs text-slate-500">
                      ${linkedInAdsData.spend?.toLocaleString() || 0} spend • {formatNumber(linkedInAdsData.clicks || 0)} clicks
                    </p>
                  )}
                  {linkedInAdsData?.cache_age_hours && (
                    <p className="text-xs text-slate-400 mt-1">
                      Cached {Math.round(linkedInAdsData.cache_age_hours)}h ago
                    </p>
                  )}
                </div>
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Organic Engagement Rate</p>
                <div className="flex items-baseline">
                  <p className="kpi-value">{linkedInData?.engagement_rate || 0}%</p>
                  <ChangeIndicator 
                    change={showChanges ? linkedInData?.changes?.engagement_rate : undefined} 
                    suffix=" pts"
                    isPercentagePoint={true}
                    comparisonLabel={socialComparisonLabel}
                  />
                </div>
                <p className="text-xs text-slate-500">
                  {formatNumber(linkedInData?.clicks || 0)} clicks
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <MousePointerClick className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-label">Page Views</p>
                <div className="flex items-baseline">
                  <p className="kpi-value">{formatNumber(typeof linkedInData?.page_views === 'object' ? (linkedInData?.page_views?.total || 0) : (linkedInData?.page_views || 0))}</p>
                  <ChangeIndicator change={showChanges ? linkedInData?.changes?.page_views : undefined} comparisonLabel={socialComparisonLabel} />
                </div>
                <p className="text-xs text-slate-500">
                  From company page
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Linkedin className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row - Only show if at least one chart is enabled */}
      {(showEngagementChart || showPageViewsChart) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Engagement Breakdown - Conditionally rendered */}
          {showEngagementChart && (
            <Card className="chart-container">
              <CardHeader>
                <CardTitle>Engagement Breakdown</CardTitle>
                <CardDescription>Likes, comments, shares, and clicks</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={engagementData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis 
                        type="number"
                        tickFormatter={isPresentationMode ? (v) => maskNumber(v) : undefined}
                      />
                      <YAxis dataKey="name" type="category" width={80} />
                      <Tooltip 
                        formatter={(value) => formatNumber(value)}
                        contentStyle={{ 
                          backgroundColor: "#fff", 
                          border: "1px solid #E2E8F0",
                          borderRadius: "8px"
                        }}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {engagementData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Page Views by Section - Hidden for tenants with show_page_views_chart: false */}
          {showPageViewsChart && (
            <Card className="chart-container">
              <CardHeader>
                <CardTitle>Page Views by Section</CardTitle>
                <CardDescription>Which sections of your page visitors view</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={pageViewsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={isPresentationMode ? (v) => maskNumber(v) : undefined} />
                      <Tooltip 
                        formatter={(value) => formatNumber(value)}
                        contentStyle={{ 
                          backgroundColor: "#fff", 
                          border: "1px solid #E2E8F0",
                          borderRadius: "8px"
                        }}
                      />
                      <Bar dataKey="views" fill={chartColors[0] || "#0A66C2"} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Detailed Stats - Conditionally render based on settings */}
      {(showAdditionalMetrics || showPageViewsTable) && (
        <div className={`grid grid-cols-1 ${showAdditionalMetrics && showPageViewsTable ? 'lg:grid-cols-3' : showPageViewsTable ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-6`}>
          {/* Engagement Stats - Always show as these are core metrics */}
          {showAdditionalMetrics && (
            <Card className="chart-container">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  Engagement Stats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Heart className="w-4 h-4 text-red-500" />
                      <span className="text-sm font-medium">Likes</span>
                    </div>
                    <span className="font-semibold">{formatNumber(linkedInData?.likes || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium">Comments</span>
                    </div>
                    <span className="font-semibold">{formatNumber(linkedInData?.comments || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Share2 className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium">Shares</span>
                    </div>
                    <span className="font-semibold">{formatNumber(linkedInData?.shares || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <MousePointerClick className="w-4 h-4 text-purple-500" />
                      <span className="text-sm font-medium">Clicks</span>
                    </div>
                    <span className="font-semibold">{formatNumber(linkedInData?.clicks || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Page Views Stats - Conditionally rendered */}
          {showPageViewsTable && (
            <Card className="chart-container">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-emerald-500" />
                  Page Views
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-medium">Desktop</span>
                    </div>
                    <span className="font-semibold">{formatNumber(linkedInData?.page_views?.desktop || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-medium">Mobile</span>
                    </div>
                    <span className="font-semibold">{formatNumber(linkedInData?.page_views?.mobile || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-medium">Overview Page</span>
                    </div>
                    <span className="font-semibold">{formatNumber(linkedInData?.page_views?.overview || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-medium">People Page</span>
                    </div>
                    <span className="font-semibold">{formatNumber(linkedInData?.page_views?.people || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mentions & Additional Stats - Conditionally rendered */}
          {showAdditionalMetrics && (
            <Card className="chart-container">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  Additional Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium">Share Mentions</span>
                    <span className="font-semibold">{formatNumber(linkedInData?.share_mentions || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium">Comment Mentions</span>
                    <span className="font-semibold">{formatNumber(linkedInData?.comment_mentions || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium">Organic Followers</span>
                    <span className="font-semibold">{formatNumber(linkedInData?.organic_followers || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium">Paid Followers</span>
                    <span className="font-semibold">{formatNumber(linkedInData?.paid_followers || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Last Synced Info */}
      {linkedInData?.last_synced && (
        <div className="mt-6 text-center text-sm text-slate-500">
          Last synced: {maskNumber(new Date(linkedInData.last_synced))}
        </div>
      )}
    </div>
  );
};

export default SocialInsightsPage;
